// @ts-nocheck
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps, StyledButton } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import Search from "@store/Library/Components/Toolkit/Icons/Search";
import Upload from "@store/Library/Components/Toolkit/Icons/Upload";
import { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { TextSeparatorPresentationProps } from "@store/Library/Components/Toolkit/TextSeparator";
import { PageErrorWrapperStyles } from "@store/Overrides/Components/PageErrorWrapper";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { formGridProps } from "@store/Themes/common/props/form";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { tableProps } from "@store/Themes/common/props/table";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { contentWidthStyles, focusStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

const templateLinkProps = mergeProps<LinkPresentationProps>(linkGetActionLinkProps(Download), {
    css: css`
        margin-top: ${x => x.theme.design.typography.spacing.small};
    `,
});

const instructionTextProps: TypographyPresentationProps = {
    forwardAs: "p",
    css: css`
        ${spacingStyles(pxToRem(16), pxToRem(16))}
    `,
};

export interface VmiDashboardProps {
    box?: ClickablePresentationProps;
    countText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    infoWrapper?: InjectableCss;
    viewMore?: ClickablePresentationProps;
}

export const vmiDashboardProps: VmiDashboardProps = {
    box: {
        css: css`
            ${boxStyles()}
            ${focusStyles()}
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${pxToRem(10, 20)};
            width: 100%;
            height: 100%;
            padding: ${pxToRem(20, 25)};
            font-weight: ${x => x.theme.design.typography.weight.bold};

            ${breakpoint("sm")} {
                flex-direction: column;
                justify-content: center;
                text-align: center;
            }
        `,
    },
    countText: {
        css: css`
            font-size: ${pxToRem(50)};
            word-break: break-all;
            line-height: 1;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: start;

            ${breakpoint("sm")} {
                align-items: center;
            }
        `,
    },
    titleText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.base};
        `,
    },
    viewMore: {
        css: css`
            ${linkActionStyles()}

            ${StyledButton}:hover & {
                color: ${getColor("text.linkHover")};
                text-decoration: underline;
            }
        `,
    },
};

export interface VmiSearchAndFilterProps {
    searchContainer?: GridContainerProps;
    searchGridItem?: GridItemProps;
    searchText?: TextFieldProps;
}

export const vmiSearchAndFilterProps: VmiSearchAndFilterProps = {
    searchContainer: {
        gap: 0,
        css: css`
            ${x => spacingStyles(null, x.theme.design.typography.spacing.large)}
        `,
    },
    searchGridItem: { width: [12, 7, 6, 5, 4] },
    searchText: { iconProps: { src: Search } },
};

export interface VmiImportModalProps {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    uploadInputGridItem?: GridItemProps;
    uploadGridContainer?: GridContainerProps;
    controlsGridItem?: GridItemProps;
    instructionGridItem?: GridItemProps;
    instructionText?: TypographyPresentationProps;
    templateLink?: LinkPresentationProps;
    uploadingModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    uploadingModalText?: TypographyPresentationProps;
    uploadsuccessModal?: ModalPresentationProps;
    uploadSuccessMessage?: InfosBoxProps;
}

export const vmiImportModalProps: VmiImportModalProps = {
    modal: { sizeVariant: "small" },
    container: formGridProps.gridContainer,
    uploadInputGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    uploadGridContainer: formGridProps.gridContainer,
    controlsGridItem: { width: 12 },
    instructionGridItem: { width: 12 },
    instructionText: instructionTextProps,
    templateLink: templateLinkProps,
    uploadingModal: {
        cssOverrides: {
            modalTitle: css`
                border-bottom: none;
                padding: 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    uploadingModalText: instructionTextProps,
    uploadsuccessModal: {
        cssOverrides: {
            modalTitle: css`
                border-bottom: none;
                padding: 0;
            `,
            modalContent: css`
                display: flex;
                flex-direction: column;
                align-items: center;
            `,
        },
    },
    uploadSuccessMessage: { type: "success" },
};

export interface VmiErrorsModalProps {
    modal?: ModalPresentationProps;
    uploadErrorMessage?: InfosBoxProps;
    tableWrapper?: InjectableCss;
    instructionText?: TypographyPresentationProps;
    templateLink?: LinkPresentationProps;
    cancelUploadButton?: ButtonPresentationProps;
}

export const vmiErrorsModalProps: VmiErrorsModalProps = {
    modal: {
        sizeVariant: "large",
        cssOverrides: {
            modalTitle: css`
                border-bottom: none;
                padding: 0;
            `,
        },
    },
    uploadErrorMessage: { type: "error" },
    tableWrapper: tableProps.wrapper,
    instructionText: instructionTextProps,
    templateLink: templateLinkProps,
    cancelUploadButton: { variant: "secondary" },
};

export interface VmiAddModalProps {
    importButton?: ButtonPresentationProps;
    separator?: TextSeparatorPresentationProps;
}

export const vmiAddModalProps: VmiAddModalProps = {
    importButton: {
        variant: "tertiary",
        icon: {
            position: "left",
            src: Upload,
        },
        css: css`
            width: 100%;
        `,
        mergeCss: true,
    },
    separator: { spacing: ["1em"] },
};

export interface VmiPageProps {
    pageErrorWrapper?: PageErrorWrapperStyles;
}

export const vmiPageProps: VmiPageProps = {
    pageErrorWrapper: {
        errorInfosBox: {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${contentWidthStyles()}
                        ${spacingStyles(pxToRem(16), pxToRem(16))}
                    `,
                },
            },
        },
    },
};
